/**
 * Richiesta fattura in carrello.
 *
 * Caricato una sola volta in cima a snippets/cart-drawer.liquid (fuori da
 * #CartDrawer), perché il markup dello snippet vive dentro #CartDrawer e viene
 * rigenerato via innerHTML a ogni update del carrello: gli <script> inseriti
 * via innerHTML non rieseguono. Tutti i listener sono delegati su document, così
 * sopravvivono ai re-render, e ogni helper rilegge il DOM ogni volta.
 *
 * Markup atteso (snippets/cart-invoice-request.liquid):
 *  - wrapper           [data-invoice-request][data-invoice-active="true|false"]
 *  - checkbox          #cart_invoice_checkbox
 *  - form              [data-invoice-form]  (attributo hidden se non attivo)
 *  - input             [data-invoice-form] input[data-attr][data-required]
 *  - messaggio errore  [data-invoice-error]
 *
 * Bottone checkout del cart drawer: #CartDrawer-Checkout (type=submit,
 * name=checkout, form="CartDrawer-Form").
 */
(() => {
  if (window.__invoiceRequestBound) return;
  window.__invoiceRequestBound = true;

  const FLAG = 'Richiesta fattura';

  const getWrapper = () => document.querySelector('[data-invoice-request]');
  const getCheckbox = () => document.getElementById('cart_invoice_checkbox');
  const getForm = () => document.querySelector('[data-invoice-form]');
  const getError = () => document.querySelector('[data-invoice-error]');
  const getInputs = () => {
    const form = getForm();
    return form ? Array.from(form.querySelectorAll('input[data-attr]')) : [];
  };
  const isActive = () => {
    const checkbox = getCheckbox();
    return !!checkbox && checkbox.checked;
  };

  // Stato dei cart attributes come reso dal server all'ultimo render.
  const serverHasInvoiceData = () => {
    const el = getWrapper();
    return !!el && el.dataset.invoiceActive === 'true';
  };

  // Stato "il carrello ha dati fattura" per come lo sa il JS: aggiornato solo
  // dopo una scrittura andata a buon fine (copre la finestra tra la nostra
  // fetch e il successivo re-render del drawer, in cui il data-attribute è
  // ancora vecchio).
  let cartHasInvoiceData = serverHasInvoiceData();
  let bypassing = false;
  let checkoutTriggered = false;

  // --- Coda serializzata di scritture sui cart attributes --------------------
  let writeChain = Promise.resolve();
  let writesInFlight = 0;

  const enqueue = (task) => {
    writesInFlight += 1;
    writeChain = writeChain
      .catch(() => {})
      .then(task)
      .catch(() => {})
      .finally(() => {
        writesInFlight -= 1;
      });
    return writeChain;
  };

  const writeInvoice = (active) => {
    const attributes = { [FLAG]: active ? 'true' : '' };
    getInputs().forEach((input) => {
      attributes[input.dataset.attr] = active ? input.value.trim() : '';
    });
    return fetch(window.routes.cart_update_url, {
      ...fetchConfig(),
      body: JSON.stringify({ attributes }),
    }).then((res) => {
      if (res && res.ok) cartHasInvoiceData = active;
      return res;
    });
  };

  // --- Validazione ---------------------------------------------------------
  const validate = (markFields, showMessage) => {
    let valid = true;
    getInputs().forEach((input) => {
      const required = input.dataset.required === 'true';
      const empty = input.value.trim() === '';
      const invalid = required && empty;
      if (invalid) valid = false;
      if (markFields) input.classList.toggle('is-invalid', invalid);
    });
    const error = getError();
    if (error && (showMessage || valid)) error.hidden = valid;
    return valid;
  };

  // --- Toggle del form + pulizia allo sflag -------------------------------
  document.addEventListener('change', (e) => {
    if (!(e.target instanceof Element) || !e.target.matches('#cart_invoice_checkbox')) return;

    const form = getForm();
    const error = getError();

    if (e.target.checked) {
      if (form) form.hidden = false;
      enqueue(() => writeInvoice(true));
    } else {
      if (form) form.hidden = true;
      if (error) error.hidden = true;
      getInputs().forEach((input) => {
        input.classList.remove('is-invalid');
        input.value = '';
      });
      enqueue(() => writeInvoice(false));
    }
  });

  // --- Salvataggio dei campi come cart attributes ------------------------
  let saveTimer = null;
  const scheduleSave = (immediate) => {
    if (!isActive()) return;
    clearTimeout(saveTimer);
    if (immediate) {
      enqueue(() => writeInvoice(true));
    } else {
      saveTimer = setTimeout(() => enqueue(() => writeInvoice(true)), 500);
    }
  };

  document.addEventListener('input', (e) => {
    if (!(e.target instanceof Element)) return;
    if (!e.target.matches('[data-invoice-form] input[data-attr]')) return;
    scheduleSave(false);
  });

  document.addEventListener('change', (e) => {
    if (!(e.target instanceof Element)) return;
    if (!e.target.matches('[data-invoice-form] input[data-attr]')) return;
    validate(true, false);
    scheduleSave(true);
  });

  // --- Blocco / reconcile al checkout ----------------------------------
  const forceCheckoutParam = (realForm) => {
    if (!realForm.querySelector('input[name="checkout"]')) {
      const hidden = document.createElement('input');
      hidden.type = 'hidden';
      hidden.name = 'checkout';
      realForm.appendChild(hidden);
    }
  };

  const proceedCheckout = () => {
    const realForm = document.getElementById('CartDrawer-Form');
    if (!realForm) return;
    bypassing = true;
    const submitter = document.getElementById('CartDrawer-Checkout');
    const validSubmitter =
      submitter && submitter.form === realForm ? submitter : null;
    try {
      if (realForm.requestSubmit && validSubmitter) {
        realForm.requestSubmit(validSubmitter);
      } else {
        forceCheckoutParam(realForm);
        realForm.submit();
      }
    } catch (err) {
      forceCheckoutParam(realForm);
      realForm.submit();
    }
    setTimeout(() => {
      bypassing = false;
      checkoutTriggered = false;
    }, 0);
  };

  const guard = (e) => {
    if (bypassing) return;

    const realForm = document.getElementById('CartDrawer-Form');
    if (!realForm) return;

    let isCheckout = false;
    if (e.type === 'submit') {
      const inCartForm =
        e.target === realForm ||
        (e.target instanceof Element && e.target.closest('#CartDrawer-Form') === realForm);
      isCheckout = inCartForm && !!e.submitter && e.submitter.id === 'CartDrawer-Checkout';
    } else if (e.target instanceof Element) {
      isCheckout = !!e.target.closest('#CartDrawer-Checkout');
    }
    if (!isCheckout) return;

    // Checkout già avviato da noi: blocca click/submit ulteriori (doppio click)
    // finché la pagina non naviga o non si torna indietro.
    if (checkoutTriggered) {
      e.preventDefault();
      e.stopImmediatePropagation();
      return;
    }

    if (isActive()) {
      e.preventDefault();
      e.stopImmediatePropagation();
      const form = getForm();
      if (form) form.hidden = false;

      if (!validate(true, true)) {
        const firstInvalid = getInputs().find((input) =>
          input.classList.contains('is-invalid'),
        );
        if (firstInvalid) firstInvalid.focus();
        return;
      }

      checkoutTriggered = true;
      enqueue(() => writeInvoice(true)).finally(proceedCheckout);
      return;
    }

    // Checkbox non attiva: se il carrello potrebbe avere dati fattura, puliscili prima.
    if (cartHasInvoiceData || serverHasInvoiceData() || writesInFlight > 0) {
      e.preventDefault();
      e.stopImmediatePropagation();
      checkoutTriggered = true;
      enqueue(() => writeInvoice(false)).finally(proceedCheckout);
    }
    // Altrimenti: nessun dato in ballo, checkout normale.
  };

  document.addEventListener('click', guard, true);
  document.addEventListener('submit', guard, true);

  // Reset degli stati transitori al ritorno sulla pagina (anche da bfcache,
  // es. tasto "indietro" dal checkout).
  window.addEventListener('pageshow', () => {
    bypassing = false;
    checkoutTriggered = false;
  });
})();
